import { useEffect, useState, useCallback } from 'react';
import { Empty, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/Button';
import {
  getEmployeeUniformProfileApi,
  getIssuancesApi,
} from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../UniformManagement/constants';

const EmpUniforms = ({ userData }) => {
  const navigate = useNavigate();
  const userId = userData?.id;

  const [profiles, setProfiles] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      getEmployeeUniformProfileApi(userId).catch(() => ({ data: { data: [] } })),
      getIssuancesApi({ company_user: userId, page_size: 100 }).catch(() => ({ data: { results: [] } })),
    ])
      .then(([profileRes, issuanceRes]) => {
        setProfiles(profileRes.data?.data || []);
        setIssuances(issuanceRes.data?.results || []);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-spinner fa-spin fa-2x" />
      </div>
    );
  }

  return (
    <div>
      {/* Uniform entitlements (profile) */}
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Uniform Entitlements</h5>
          <small className="text-muted">{profiles.length} item(s)</small>
        </div>
        <div className="card-body">
          {profiles.length === 0 ? (
            <Empty description="No uniforms assigned to this employee's position." />
          ) : (
            <div className="row">
              {profiles.map(p => (
                <div key={p.id} className="col-md-6 col-lg-4 mb-3">
                  <div className="d-flex align-items-center p-2 border rounded">
                    {p.uniform_item_image && (
                      <img src={p.uniform_item_image} alt={p.uniform_item_name}
                        style={{ width: 50, height: 50, objectFit: 'contain', marginRight: 12 }} />
                    )}
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{p.uniform_item_name}</div>
                      <small className="text-muted">
                        Size: {p.size_detail ? `${p.size_detail.name} (${p.size_detail.code})` : (
                          <Tag color="orange">Pending</Tag>
                        )}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Issuance history */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Issuance History</h5>
          <small className="text-muted">{issuances.length} record(s)</small>
        </div>
        <div className="card-body p-0">
          {issuances.length === 0 ? (
            <div className="py-4">
              <Empty description="No issuances yet." />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Uniform</th>
                    <th>Size</th>
                    <th>Qty</th>
                    <th>Issued</th>
                    <th>Replacement Due</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {issuances.map(i => (
                    <tr key={i.id}>
                      <td>{i.uniform_item_name}</td>
                      <td>{i.size_detail ? i.size_detail.code : '—'}</td>
                      <td>{i.quantity}</td>
                      <td>{formatDateHandler(i.issuance_date)}</td>
                      <td>{i.next_replacement_date ? formatDateHandler(i.next_replacement_date) : '—'}</td>
                      <td><span style={getStatusBadge(i.status)}>{i.status}</span></td>
                      <td>
                        <Button
                          label="View"
                          icon="fa fa-eye"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/uniform-management/issuance/${i.id}`)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmpUniforms;
